<script setup lang="ts">
import { computed, onMounted, reactive, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { useRoute } from 'vue-router'
import { useTheme } from 'vuetify'

import AppBarAuthenticated from '@/components/AppBarAuthenticated.vue'
import AppBarPlain from '@/components/AppBarPlain.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import RecorderWindow from '@/components/RecorderWindow.vue'
import Snackbar from '@/components/SnackBar.vue'
import { useUtil } from '@/composables/util'
import { LS_THEME_KEY, THEME_LIGHT, type ThemeName, VALID_THEMES } from '@/constants/practiceConstants'
import store from '@/store'

const logger = useLogger()
const route = useRoute()
const PAGES_WITHOUT_NAVBAR = ['index'] as Array<string>
const PAGES_WITHOUT_FOOTER = [] as Array<string>

const { forceLogout } = useUtil(logger)

import faviconDark from './assets/images/favicon-dark.png'
import faviconLight from './assets/images/favicon-light.png'

const state = reactive({
  favicons: {
    dark: faviconDark,
    light: faviconLight,
  },
})

const pageWithAppBar = computed<boolean>(() => {
  return !PAGES_WITHOUT_NAVBAR.includes(route.name as string)
})

const pageWithFooter = computed<boolean>(() => {
  return !PAGES_WITHOUT_FOOTER.includes(route.name as string)
})

onMounted(() => {
  const vueTheme = useTheme()
  const storedTheme = localStorage[LS_THEME_KEY] as string
  vueTheme.global.name.value = VALID_THEMES.includes(storedTheme as ThemeName) ? storedTheme : THEME_LIGHT
  const headElt = document.querySelector('head')

  //
  // set favicon
  //

  // https://stackoverflow.com/questions/56393880/how-do-i-detect-dark-mode-using-javascript
  const theme = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

  // https://spemer.com/articles/set-favicon-with-javascript.html
  const faviconElt = document.createElement('link')
  faviconElt.setAttribute('rel', 'shortcut icon')
  faviconElt.setAttribute('href', state.favicons[theme])
  faviconElt.setAttribute('media', `(prefers-color-scheme: ${theme})`)
  headElt!.appendChild(faviconElt)

  watch(
    () => store.getters.user.userIsLoggedIn,
    (newValue, oldValue) => {
      // user logged out
      if (oldValue && !newValue) {
        logger.debug('forceLogout from watch store.getters.user.userIsLoggedIn')
        forceLogout()
        return
      }
    },
  )
})
</script>

<template>
  <v-app>
    <template v-if="pageWithAppBar">
      <template v-if="!store.getters.user.userIsLoggedIn">
        <app-bar-plain />
      </template>
      <template v-else>
        <app-bar-authenticated />
      </template>
    </template>
    <v-main>
      <v-container fluid>
        <recorder-window />
        <router-view />
        <v-footer v-if="pageWithFooter" app color="primary">
          <v-row align="center" justify="center" no-gutters>
            <v-btn class="mx-4 my-2" color="white" size="x-small" to="/about" variant="text"> About </v-btn>
            <v-btn class="mx-4 my-2" color="white" size="x-small" to="/contact" variant="text"> Contact </v-btn>
          </v-row>
        </v-footer>
      </v-container>
      <snackbar />
      <confirm-dialog />
    </v-main>
  </v-app>
</template>

<style>
@import './assets/vuetify/roboto-font.css';
@import './assets/vuetify/materialdesignicons.min.css';

:root {
  /** Height of the Vuetify app bar. Sticky toolbars offset by this to sit flush below it. */
  --app-bar-height: 62px;
}

#app {
  font-family: Roboto, 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Brand class used in IndexView section headers */
.bmt {
  color: rgb(var(--v-theme-primary));
}

/* Links inherit brand color */
a {
  color: rgb(var(--v-theme-primary));
}
a:hover {
  color: rgb(var(--v-theme-secondary));
}

/* Colorize button with diagonal rainbow stripes — faint when inactive, vivid when active */
.colorize-btn {
  position: relative;
  overflow: hidden;
  color: rgb(var(--v-theme-on-surface)) !important;
  border-color: rgba(var(--v-theme-on-surface), 0.25) !important;
}

.colorize-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    135deg,
    rgba(255, 0, 0, 0.08) 0px,
    rgba(255, 165, 0, 0.08) 20px,
    rgba(255, 255, 0, 0.08) 40px,
    rgba(0, 180, 0, 0.08) 60px,
    rgba(0, 100, 255, 0.08) 80px,
    rgba(130, 0, 255, 0.08) 100px,
    rgba(255, 0, 0, 0.08) 120px
  );
  pointer-events: none;
}

.colorize-btn.v-btn--active {
  border-color: rgba(var(--v-theme-on-surface), 0.5) !important;
}

.colorize-btn.v-btn--active::before {
  background: repeating-linear-gradient(
    135deg,
    rgba(255, 0, 0, 0.35) 0px,
    rgba(255, 165, 0, 0.35) 20px,
    rgba(255, 255, 0, 0.35) 40px,
    rgba(0, 180, 0, 0.35) 60px,
    rgba(0, 100, 255, 0.35) 80px,
    rgba(130, 0, 255, 0.35) 100px,
    rgba(255, 0, 0, 0.35) 120px
  );
}

/* Fix compact-picker list padding from @lk77/vue3-color */
.vc-compact ul {
  padding-left: 0px !important;
}

/* Subtle page transition */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.15s ease;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
